import { useEffect, useState, useMemo } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * ThinkingLoader — inline "AI is working" indicator with rotating, playful
 * status messages. Long AI tasks (match-scoring, letter/prep generation,
 * bulk insight refresh) can take 5-30 seconds; a single static "loading…"
 * feels broken. Rotating human-tone copy ("Ik lees jouw CV grondig door…",
 * "Rood potlood erbij pakken…") turns dead-time into a beat that shows
 * the app is thinking, à la Emergent's "sprinkling magic" / "tightening
 * screws".
 *
 * Usage:
 *   <ThinkingLoader stage="match" />                 // preset copy list
 *   <ThinkingLoader messages={["..","..",".."]} />   // custom copy list
 *   <ThinkingLoader stage="letter" size="sm" />      // inline in a button row
 *
 * Props:
 *   stage    — preset copy set: "match" | "letter" | "prep" | "insight"
 *                              | "bulk" | "resume" | "email" | "generic"
 *   messages — override with a custom string[] (takes precedence over stage)
 *   size     — "sm" | "md" (default "md")
 *   interval — ms between message swaps (default 2200)
 *   testId   — data-testid for the wrapper
 */
const STAGE_KEYS = {
  match: [
    "thinkMatch1", "thinkMatch2", "thinkMatch3", "thinkMatch4",
    "thinkMatch5", "thinkMatch6", "thinkMatch7",
  ],
  letter: [
    "thinkLetter1", "thinkLetter2", "thinkLetter3", "thinkLetter4",
    "thinkLetter5", "thinkLetter6",
  ],
  prep: [
    "thinkPrep1", "thinkPrep2", "thinkPrep3", "thinkPrep4",
    "thinkPrep5", "thinkPrep6",
  ],
  insight: [
    "thinkInsight1", "thinkInsight2", "thinkInsight3", "thinkInsight4",
    "thinkInsight5",
  ],
  bulk: [
    "thinkBulk1", "thinkBulk2", "thinkBulk3", "thinkBulk4",
    "thinkBulk5", "thinkBulk6",
  ],
  resume: [
    "thinkResume1", "thinkResume2", "thinkResume3", "thinkResume4",
    "thinkResume5", "thinkResume6",
  ],
  email: [
    "thinkEmail1", "thinkEmail2", "thinkEmail3", "thinkEmail4",
  ],
  generic: [
    "thinkGeneric1", "thinkGeneric2", "thinkGeneric3", "thinkGeneric4",
    "thinkGeneric5",
  ],
};

export default function ThinkingLoader({
  stage = "generic",
  messages,
  size = "md",
  interval = 2200,
  testId = "thinking-loader",
  className = "",
}) {
  const { t } = useT();
  const list = useMemo(() => {
    if (Array.isArray(messages) && messages.length > 0) return messages;
    const keys = STAGE_KEYS[stage] || STAGE_KEYS.generic;
    return keys.map((k) => t(k));
  }, [messages, stage, t]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (list.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % list.length), interval);
    return () => clearInterval(id);
  }, [list.length, interval]);

  const iconSize = size === "sm" ? 12 : 14;
  const textCls =
    size === "sm"
      ? "text-[0.7rem] font-mono uppercase tracking-[0.14em]"
      : "text-xs font-mono uppercase tracking-[0.14em]";

  return (
    <span
      role="status"
      aria-live="polite"
      data-testid={testId}
      className={`inline-flex items-center gap-2 text-neutral-600 ${className}`}
    >
      <span className="relative inline-flex items-center justify-center">
        <Loader2 size={iconSize} strokeWidth={1.5} className="animate-spin" />
        <Sparkles
          size={iconSize - 2}
          strokeWidth={1.75}
          className="absolute text-amber-500 animate-pulse opacity-70"
          aria-hidden="true"
        />
      </span>
      <span
        key={idx}
        data-testid={`${testId}-msg`}
        className={`${textCls} thinking-fade`}
      >
        {list[idx] || t("thinkGeneric1")}
      </span>
    </span>
  );
}
